import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Film } from "lucide-react";
import { toast } from "react-toastify";
import { useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";

const EditLeftPanel = ({
  slides,
  activeSlide,
  setActiveSlide,
  isReorderOnly,
  isDragging,
  isProcessing,
  slidesAnimationData,
  uuid,
  setSlides,
  setIsProcessing,
  setIsDragging,
  draggedIndex,
  setDraggedIndex,
  setDeletedSlides,
  setSlidesAnimationData,
  setAnimatedSlides,
}) => {
  const processPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const slidesFromPDF = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      const fileBlob = new File([blob], `${file.name}_page_${i}.png`, {
        type: "image/png",
      });

      const imageFile = new File([blob], `${file.name}_page_${i}.png`, {
        type: "image/png",
      });

      slidesFromPDF.push({
        id: `pdf-${Date.now()}-${i}`,
        type: "image",
        title: `${file.name} - Page ${i}`,
        is_active: true,
        presentationId: uuid,
        isNew: true,

        image_file: imageFile,

        image: {
          image_url: URL.createObjectURL(blob),
          image_title: imageFile.name,
        },
      });

      page.cleanup();
    }

    return slidesFromPDF;
  };

  const handleUploadImage = (e) => {
    const files = Array.from(e.target.files);
    const newSlides = files.map((file) => ({
      type: "image",
      title: file.name,
      image_title: file.name,
      image_file: file,
      is_active: true,
      image: {
        image_url: URL.createObjectURL(file),
      },
      presentationId: uuid,
      isNew: true,
    }));

    setSlides((prev) => [...prev, ...newSlides]);
    if (activeSlide === null && newSlides.length > 0) setActiveSlide(0);
  };
  // ✅ Handle PDF file upload
  const handleUploadPDF = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please upload a valid PDF file.");
      return;
    }
    setIsProcessing(true);
    try {
      const pages = await processPDF(file);
      setSlides((prev) => [...prev, ...pages]);
      if (activeSlide === null && pages.length > 0) setActiveSlide(0);
    } catch (err) {
      toast.error("Failed to process PDF. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      for (const file of files) {
        if (file.type === "application/pdf") {
          setIsProcessing(true);
          const pages = await processPDF(file);
          setSlides((prev) => [...prev, ...pages]);
          setIsProcessing(false);
        } else if (file.type.startsWith("image/")) {
          const newSlide = {
            id: Date.now() + Math.random(),
            type: "image",
            title: file.name,
            image_title: file.name,
            image_file: file,
            is_active: true,
            image: {
              image_url: URL.createObjectURL(file),
            },
            isNew: true,
          };
          setSlides((prev) => [...prev, newSlide]);
        }
      }
      if (activeSlide === null && slides.length > 0) setActiveSlide(0);
    },
    [activeSlide, slides.length],
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDropSlide = (index) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const updatedSlides = [...slides];
    const [movedSlide] = updatedSlides.splice(draggedIndex, 1);
    updatedSlides.splice(index, 0, movedSlide);
    const finalizedSlides = updatedSlides.map((slide, i) => ({
      ...slide,
      order: i + 1,
    }));
    setSlides(finalizedSlides);
    setDraggedIndex(null);
  };

  const handleDeleteSlide = (id) => {
    setSlides((prevSlides) => {
      const updatedSlides = prevSlides.filter((slide) => slide.id !== id);
      const reorderedSlides = updatedSlides.map((slide, index) => ({
        ...slide,
        order: index + 1,
      }));
      return reorderedSlides;
    });

    // Add deleted slide ID to the deletedSlides array
    if (id) {
      setDeletedSlides((prev) => [...prev, id]);
    }

    // Adjust active slide
    setActiveSlide((prevActive) => {
      const newSlides = slides.filter((s) => s.id !== id);
      if (newSlides.length === 0) return null;
      if (prevActive === null) return null;
      if (prevActive >= newSlides.length) return newSlides.length - 1;
      return prevActive;
    });
  };

  const handleAddBlankSlide = () => {
    const newIndex = slides.length;
    const newSlide = {
      type: "blank",
      title: "Animated Slide",
      is_active: true,
      presentationId: uuid,
      isNew: true,
    };
    setSlides((prev) => [...prev, newSlide]);
    setSlidesAnimationData((prev) => ({
      ...prev,
      [newIndex]: {
        elements: [],
        interactions: [],
        totalDuration: 3.0,
        canvasBackground: null,
        canvasBackgroundColor: "#1a1a2e",
      },
    }));
    setAnimatedSlides((prev) => ({ ...prev, [newIndex]: true }));
    setActiveSlide(newIndex);
  };

  return (
    <div
      className="col-span-3 bg-white border-r border-gray-200 p-4 overflow-y-auto rounded-2xl h-[38rem]"
      style={{ scrollbarWidth: "thin" }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Slides</h2>
        <span className="text-gray-500 text-sm">{slides.length} slides</span>
      </div>

      {!isReorderOnly && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`w-full h-auto mb-4 p-5 flex flex-col items-center justify-center border-2 border-dashed rounded-xl transition ${
            isDragging
              ? "border-purple-500 bg-purple-50"
              : "border-lightPurple bg-gray-50"
          }`}
        >
          <p className="text-gray-500 text-sm mb-2">
            Drag & Drop <span className="font-semibold">PDF or Image</span> here
          </p>

          <div className="flex gap-4 flex-wrap justify-center mt-2">
            <label className="flex items-center justify-center px-4 py-2 bg-lightPurple text-white rounded-md text-sm cursor-pointer hover:bg-purpleColor transition">
              <FontAwesomeIcon icon={faPlus} className="text-xs mr-2" />
              Add PDF
              <input
                type="file"
                accept="application/pdf"
                onChange={handleUploadPDF}
                className="hidden"
              />
            </label>

            <label className="flex items-center justify-center px-4 py-2 bg-lightPurple text-white rounded-md text-sm cursor-pointer hover:bg-purpleColor transition">
              <FontAwesomeIcon icon={faPlus} className="text-xs mr-2" />
              Add Image
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleUploadImage}
                className="hidden"
              />
            </label>

            <button
              onClick={handleAddBlankSlide}
              className="flex items-center justify-center px-4 py-2 bg-lightPurple text-white rounded-md text-sm hover:bg-purpleColor transition"
            >
              <Film className="w-3.5 h-3.5 mr-2" />
              Add Blank Slide
            </button>
          </div>
        </div>
      )}

      {isProcessing && (
        <p className="text-center text-sm text-gray-500 my-4">
          Processing PDF, please wait...
        </p>
      )}

      {slides.map((slide, index) => (
        <div
          key={slide.id || index}
          draggable
          onClick={() => setActiveSlide(index)}
          onDragStart={() => handleDragStart(index)}
          onDragOver={handleDragOver}
          onDrop={() => handleDropSlide(index)}
          className={`mb-3 rounded-lg cursor-pointer transition border overflow-hidden relative ${
            activeSlide === index
              ? "border-2 border-lightPurple shadow-md bg-purple-50"
              : "border border-gray-200 hover:border-gray-400"
          } ${!slide.is_active ? "opacity-60 grayscale-[0.5]" : ""}`}
        >
          {!slide.is_active && (
            <div className="absolute top-1 left-1 bg-gray-500 text-white text-[10px] px-1.5 py-0.5 rounded-md font-bold z-10">
              HIDDEN
            </div>
          )}

          {!isReorderOnly && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSlide(slide.id);
              }}
              className="absolute top-1 right-1 bg-slate-200 font-bold text-black rounded-md w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 hover:text-white transition"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}

          {/* Keep your existing slide preview content here */}
          <div className="bg-gray-50 w-full h-40 flex items-center justify-center overflow-hidden rounded-lg border border-gray-200">
            {slide.image?.image_url && (
              <img
                src={slide.image.image_url}
                alt={slide.title}
                className="object-contain w-full h-full"
              />
            )}

            {slide.type === "blank" && (
              <div
                className="w-full h-full flex flex-col items-center justify-center gap-1.5"
                style={{
                  backgroundColor:
                    slidesAnimationData[index]?.canvasBackgroundColor ||
                    "#1a1a2e",
                }}
              >
                <Film className="w-5 h-5 text-white/40" />
                <span className="text-[10px] text-white/40 font-semibold uppercase">
                  Animated
                </span>
              </div>
            )}

            {(slide.question?.question_type === "multiple" ||
              slide.question?.question_type === "poll") && (
              <div
                className="p-2 w-full"
                style={{
                  ...(slidesAnimationData?.[index]?.canvasBackground
                    ? {
                        backgroundImage: `url("${slidesAnimationData[index].canvasBackground}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }
                    : {
                        backgroundColor:
                          slidesAnimationData?.[index]?.canvasBackgroundColor ||
                          "#f3f4f6",
                      }),
                }}
              >
                <p className="font-bold text-base mb-2 ml-1 text-black">
                  {slide.title}
                </p>
                <h2 className="text-base font-bold mb-2 ml-1">
                  {slide.question.question_text}
                </h2>
                <ul>
                  {slide.question.choices &&
                    slide?.question.choices?.map((choice, i) => (
                      <li
                        key={i}
                        className="p-2 border rounded-md hover:bg-gray-100 cursor-pointer flex items-center space-x-2 my-2"
                      >
                        <input
                          readOnly
                          type={
                            slide.question?.question_type === "poll"
                              ? "radio"
                              : "checkbox"
                          }
                          name={`choice-${slide.id}`}
                          id={`choice-${i}`}
                          value={choice}
                          className={`h-2 w-2 ${
                            slide.question?.question_type === "poll"
                              ? "form-radio"
                              : "form-checkbox"
                          } text-lightPurple`}
                        />
                        <label
                          htmlFor={`choice-${i}`}
                          className="text-gray-700"
                        >
                          {typeof choice === "object"
                            ? choice.choice_text
                            : choice}
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {slide.question?.question_type === "text" && (
              <div
                className="p-2 w-full"
                style={{
                  ...(slidesAnimationData?.[index]?.canvasBackground
                    ? {
                        backgroundImage: `url("${slidesAnimationData[index].canvasBackground}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }
                    : {
                        backgroundColor:
                          slidesAnimationData?.[index]?.canvasBackgroundColor ||
                          "#f3f4f6",
                      }),
                }}
              >
                <p className="font-bold text-base mb-2 ml-1 text-black">
                  {slide.title}
                </p>
                <h2 className="text-base font-bold mb-2 ml-1">
                  {slide.question.question_text}
                </h2>
                <textarea
                  readOnly
                  placeholder="Answer..."
                  className="w-full border rounded p-1 text-[11px] resize-none h-10"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between bg-white px-2 py-1 text-[11px] text-gray-600">
            <span className="font-medium">{index + 1}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EditLeftPanel;
